import React, { useEffect, useState, useCallback } from 'react';
import dateFormat from 'dateformat';
import { useParams } from 'react-router-dom';
import Radio from '@mui/material/Radio';
import Checkbox from '@mui/material/Checkbox';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import DialogActions from '@mui/material/DialogActions';
import CloseIcon from '@mui/icons-material/Close';
import { downloadFile } from '#action/download';
import { formatter } from '#component/shipment/helper';
import buildReportPath, { outputFormatOptions } from './build-report-path';
import {
  StyledDialog,
  StyledIconButton,
  StyledDialogTitle,
  StyledDialogContent,
  StyledRadioGroup,
  Divider
} from './styles';

const defaultContentOptions = [
  {
    name: 'locations',
    label: 'Map / Locations',
    checked: false
  },
  {
    name: 'timeline',
    label: 'Timeline',
    checked: true
  },
  {
    name: 'temperatureChart',
    label: 'Temperature chart',
    checked: true,
    disabled: true
  },
  {
    name: 'humidityChart',
    label: 'Humidity chart',
    checked: false
  },
  {
    name: 'statisticalSummary',
    label: 'Statistical summary',
    checked: true
  },
  {
    name: 'alertsTable',
    label: 'Alerts table',
    checked: true,
    disabled: true
  },
  {
    name: 'measurementsTable',
    label: 'Measurements table',
    checked: false
  },
  {
    name: 'cargo',
    label: 'Cargo',
    checked: false
  },
  {
    name: 'customFields',
    label: 'Custom fields',
    checked: false
  }
];

const ReportDialog = props => {
  const {
    showDialog,
    setReportDialog,
    shipment: { id: shipmentId, label, uid, files: shipmentFiles },
    dispatch,
    account,
    dateRange
  } = props;

  const { token } = useParams(); // deeplink page

  const [measurementSystemValue, setMeasurementSystem] = useState(account.measurementSystem);

  const [outputFormat, setOutputFormat] = useState(outputFormatOptions.PDF);
  const [contentOptions, setContentOptions] = useState(defaultContentOptions);
  const [fileOptions, setFileOptions] = useState(
    shipmentFiles?.map(file => ({ ...file, checked: false }))
  );

  useEffect(() => setMeasurementSystem(account.measurementSystem), [account.measurementSystem]);

  const closeDialog = () => setReportDialog(false);
  const handleContentOptionsChange = useCallback(e => {
    const updatedContentOptions = contentOptions.map(option =>
      (option.name === e.target.name ? { ...option, checked: e.target.checked } : option));
    setContentOptions(updatedContentOptions);
  }, [contentOptions]);

  const handleFileOptionsChange = e => {
    const newImages = fileOptions.map(file =>
      (file.id.toString() === e.target.id ? { ...file, checked: e.target.checked } : file));
    setFileOptions(newImages);
  };

  const handleExportClick = async () => {
    closeDialog();
    const path = buildReportPath({
      outputFormat,
      token,
      shipmentId,
      measurementSystem: measurementSystemValue,
      contentOptions,
      fileOptions
    });

    const shipmentLabel = label || formatter(uid);

    await dispatch(downloadFile(
      path,
      `${shipmentLabel}_${dateFormat(dateRange[0], 'dd-mm-yyyy')}_${dateFormat(dateRange[1], 'dd-mm-yyyy')}${outputFormat === outputFormatOptions.CSV ? '.csv' : '.pdf'}`
    ));
  };

  return (
    <StyledDialog
      open={showDialog}
      onClose={closeDialog}
      aria-labelledby="report-dialog"
      aria-describedby="report-dialog"
      onClick={e => e.stopPropagation()}
    >
      <StyledDialogTitle>
        Report options
        <StyledIconButton aria-label="close" onClick={closeDialog}>
          <CloseIcon />
        </StyledIconButton>
      </StyledDialogTitle>
      <StyledDialogContent>
        <Typography variant="h6">Units</Typography>
        <StyledRadioGroup
          row
          aria-label="Measurement system"
          name="measurementSystem"
          value={measurementSystemValue}
          onChange={e => setMeasurementSystem(e.target.value)}
        >
          <FormControlLabel value="metric" control={<Radio />} label="Metric (km, °C)" />
          <FormControlLabel value="us" control={<Radio />} label="Imperial (mi, °F)" />
        </StyledRadioGroup>
        <Typography variant="h6">Output format</Typography>
        <StyledRadioGroup
          row
          aria-label="outputFormat"
          name="outputFormat"
          value={outputFormat}
          onChange={e => setOutputFormat(e.target.value)}
        >
          {Object.entries(outputFormatOptions).map(([key, value]) =>
            <FormControlLabel
              key={key}
              value={value}
              control={<Radio />}
              label={value.toUpperCase()}
            />)}
        </StyledRadioGroup>
        {outputFormat === outputFormatOptions.PDF &&
          <>
            <Typography variant="h6">Content</Typography>
            <FormGroup>
              {contentOptions
                .map(({ name, label: formControlLabel, checked, disabled }) => (
                  <FormControlLabel
                    key={name}
                    control={
                      <Checkbox
                        checked={checked}
                        onChange={handleContentOptionsChange}
                        name={name}
                        disabled={disabled}
                      />
                    }
                    label={formControlLabel}
                  />
                ))}
              {fileOptions?.length > 0 &&
              <>
                <Divider />
                {fileOptions.map(({ name, id, checked }) => <FormControlLabel
                  key={id}
                  control={<Checkbox
                    checked={checked}
                    onChange={handleFileOptionsChange}
                    name={name}
                    id={id.toString()}
                  />}
                  label={name}
                />)}
              </>}
            </FormGroup>
          </>}
      </StyledDialogContent>
      <DialogActions>
        <Button onClick={closeDialog} variant="outlined" color="primary">
          Cancel
        </Button>
        <Button onClick={handleExportClick} variant="contained" color="secondary" autoFocus>
          Download
        </Button>
      </DialogActions>
    </StyledDialog>
  );
};

export default ReportDialog;
